import { EventEmitter, Component, Output, Input } from '@angular/core';
import { AbstractControl, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { TranslateService } from '@ngx-translate/core';
import { IStockFileEntry, IStockFolder, IxellAPIService } from 'src/app/tools/api.service';
import { FileTranslateModalResult, GroupedLocale, LocalePair } from '../cloud/cloud.component';
import { checkForExtension, CLOUD_MAX_FILE_SIZE, MIN_COUNTRY_MODE } from '../utils/utils';


@Component({
    selector: 'cloud-translate-modal',
    templateUrl: './translate-modal.component.html',
    styleUrls: ['./translate-modal.component.scss']
})

export class CloudTranslateModalComponent {
    CLOUD_MAX_FILE_SIZE = CLOUD_MAX_FILE_SIZE;

    @Input() folder: IStockFolder;
    @Input() file: IStockFileEntry;
    @Input() possibleLocales: { [key: number]: GroupedLocale };
    @Input() uploadProgress: number;
    @Input() selectedCountry: number;
    @Input() selectedLanguage: number;
    @Input() languageRedirects: { [lang: number]: number };
    @Output() close = new EventEmitter<void>();
    @Output() save = new EventEmitter<FileTranslateModalResult>();
    @Output() reset = new EventEmitter<void>();

    language: string;

    selectedFile: File = null;
    readonly selectedFilename = new FormControl<string>('', [Validators.required], [async (control: AbstractControl): Promise<ValidationErrors | null> => {
        if (!this.selectedFile || this.fileKind === 1)
            return null;

        if (this.selectedFile.size > CLOUD_MAX_FILE_SIZE)
            return { fileTooBig: true };

        if (!await checkForExtension(this.selectedFile, this.folder.extensions))
            return { invalidExtension: true };

        return null;
    }]);
    readonly chosenTitle = new FormControl<string>('', [Validators.required]);

    formsDisabled: boolean = false;
    formsGlobalError: string = '';

    isTranslated: boolean = false;
    fileKind = 1;

    circularError: LocalePair[] = [];

    private langueSub: any;

    constructor(private readonly apiService: IxellAPIService, private readonly translateService: TranslateService) { }

    ngOnInit() {
        const linkedContent = this.getFileContent(this.file, this.folder.mode);
        if (!linkedContent || !linkedContent.content) { // Entrée inexistante => Non publié, ou Redirection => Redirigé
            this.fileKind = 0; // Pas de radio de fichier
            this.isTranslated = (this.selectedLanguage === 2); // Cas spécifique de l'anglais
            this.chosenTitle.setValue('');
        }
        else {
            this.fileKind = 1; // Fichier existant
            this.isTranslated = true;
            this.chosenTitle.setValue(linkedContent.name);
        }

        this.changeTranslationState({ source: null, value: this.isTranslated });
    }

    ngOnDestroy() {
    }

    changeTranslationState(event: MatRadioChange) {
        if (event.value) {
            this.selectedFilename.enable();
            this.chosenTitle.enable();
        }
        else {
            this.selectedFilename.disable();
            this.chosenTitle.disable();
        }
    }

    onFileSelected(fileInput: HTMLInputElement) {
        this.selectedFile = (fileInput.files.length > 0) ? fileInput.files[0] : null;
        this.selectedFilename.setValue((fileInput.files.length > 0) ? fileInput.files[0].name : null);
        this.selectedFilename.markAsTouched();
    }

    onFileReset(fileInput: HTMLInputElement) {
        fileInput.value = null;
        fileInput.dispatchEvent(new Event('change'));
    }

    getFileContent(file: IStockFileEntry, mode: number) {
        return file.contents.find(ctn => (mode < MIN_COUNTRY_MODE || ctn.paysId === this.selectedCountry) && (mode >= MIN_COUNTRY_MODE || ctn.langId === this.selectedLanguage));
    }

    getDefaultFileContent(file: IStockFileEntry, mode: number) {
        return (mode < MIN_COUNTRY_MODE) ? file.contents.find(ctn => ctn.langId === this.languageRedirects[this.selectedLanguage]) : null;
    }

    getDownloadableFileContent(file: IStockFileEntry, mode: number) {
        const ctn = this.getFileContent(file, mode);
        if (!ctn.content.length) {
            return this.getDefaultFileContent(file, mode);
        }

        return ctn;
    }

    downloadFile() {
        const ctn = this.getDownloadableFileContent(this.file, this.folder.mode);
        if (ctn)
            window.open(ctn.url, "_blank");
    }

    doClose() {
        this.close.emit();
    }

    async doSave() {
        this.formsGlobalError = '';
        this.reset.emit();

        if (this.fileKind === 1)
            this.selectedFilename.markAsUntouched();

        if (this.isTranslated && ((this.fileKind !== 1 && this.selectedFilename.invalid) || this.chosenTitle.invalid)) {
            if (this.fileKind !== 1)
                this.selectedFilename.markAsTouched();

            this.chosenTitle.markAsTouched();
            return;
        }

        this.save.emit({
            file: this.file,
            folder: this.folder,
            data: {
                country: this.selectedCountry,
                language: this.selectedLanguage,
                title: (this.isTranslated) ? this.chosenTitle.value : null,
                file: (this.isTranslated && this.fileKind !== 1) ? this.selectedFile : null
            },
            formStatusChanger: (state: boolean | string) => this.changeFormState(state)
        });
    }

    changeFormState(state: boolean | string) {
        if (typeof (state) === 'string') {
            this.formsGlobalError = state;
            this.changeFormState(false);
            return;
        }

        this.formsDisabled = state;

        if (state) {
            this.selectedFilename.disable();
            this.chosenTitle.disable();
        }
        else {
            this.selectedFilename.enable();
            this.chosenTitle.enable();
        }
    }
}
